/**
 * 富の神 × 風の洞察 — Guided Trading Command Center
 *
 * Tomi-no-Kami generates signals graded by Fibonacci conviction.
 * Kaze-no-Dōsatsu powers all market intelligence.
 * Bi-no-Kisu aesthetic: golden proportions, sovereign precision.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  AutoGraph,
  Refresh,
  TrendingUp,
  TrendingDown,
  Remove,
  Air,
  CurrencyBitcoin,
  AccountBalance,
  Timeline,
  Psychology,
  Bolt,
  ExpandMore,
  ExpandLess,
  Send,
  EmojiEvents,
  Insights,
  ShowChart,
  Business,
  Analytics,
  PlayArrow,
  Stop,
  RadioButtonChecked,
  CheckCircle,
  Error as ErrorIcon,
  SkipNext,
  MonitorHeart,
} from '@mui/icons-material';
import tomiService, {
  TomiSignal,
  OptionsSignal,
  SignalsData,
  SpotPortfolio,
  LearningStats,
  AlpacaPosition,
  AutopilotMode,
  AutopilotSession,
  AutopilotDecision,
} from '../services/tomiService';
import TomiV2ShadowPanel from '../components/TomiV2ShadowPanel';

// ─── Design System (Bi no Kisu) ─────────────────────────

const COLORS = {
  bg: '#07070f',
  surface: '#0e0e1c',
  surface2: '#13132a',
  border: '#1e1e3a',
  gold: '#c8951a',
  goldLight: '#e8b84b',
  goldDim: '#6b4e0e',
  blue: '#4a7fff',
  blueDim: '#1a2d5a',
  green: '#2ecc71',
  greenDim: '#0e4a26',
  red: '#e05252',
  redDim: '#4a1515',
  amber: '#f0a500',
  platinum: '#e0e0e8',
  muted: '#5a5a7a',
};

// Fibonacci ratios with labels
const FIB_LEVELS = [
  { ratio: 0.618, label: '61.8%', color: COLORS.gold, desc: 'Primary' },
  { ratio: 0.382, label: '38.2%', color: COLORS.blue, desc: 'Secondary' },
  { ratio: 0.236, label: '23.6%', color: '#9b59b6', desc: 'Tertiary' },
  { ratio: 0.146, label: '14.6%', color: COLORS.green, desc: 'Quarter' },
  { ratio: 0.090, label: '9.0%', color: COLORS.amber, desc: 'Sentinel' },
];

// ─── Helper Functions ────────────────────────────────────

function getActionColor(action: string): string {
  if (action === 'BUY' || action === 'LONG') return COLORS.green;
  if (action === 'SELL' || action === 'SHORT') return COLORS.red;
  if (action === 'WATCH') return COLORS.amber;
  return COLORS.muted;
}

function getActionIcon(action: string) {
  if (action === 'BUY' || action === 'LONG') return <TrendingUp sx={{ fontSize: 16 }} />;
  if (action === 'SELL' || action === 'SHORT') return <TrendingDown sx={{ fontSize: 16 }} />;
  if (action === 'WATCH') return <Timeline sx={{ fontSize: 16 }} />;
  return <Remove sx={{ fontSize: 16 }} />;
}

function formatUsd(val: number): string {
  if (!val && val !== 0) return '—';
  return val.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

function formatPrice(val: number): string {
  if (!val) return '—';
  if (val > 1000) return `$${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (val > 1) return `$${val.toFixed(4)}`;
  return `$${val.toFixed(6)}`;
}

function scoreBar(score: number, color: string) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <LinearProgress
        variant="determinate"
        value={score * 100}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.07)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
        }}
      />
      <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 36, textAlign: 'right' }}>
        {(score * 100).toFixed(0)}%
      </Typography>
    </Box>
  );
}

// ─── Fibonacci Portfolio Visualizer ─────────────────────

function FibonacciAllocation({
  signals,
  capital,
  cashReserve,
}: {
  signals: TomiSignal[];
  capital: number;
  cashReserve: number;
}) {
  const actionable = signals.filter(s => s.dollar_allocation > 0);

  return (
    <Box>
      <Typography variant="caption" sx={{ color: COLORS.muted, letterSpacing: 2, textTransform: 'uppercase', display: 'block', mb: 2 }}>
        φ Fibonacci Capital Allocation
      </Typography>

      {/* Golden ratio visual bar */}
      <Box sx={{ display: 'flex', height: 10, borderRadius: 2, overflow: 'hidden', mb: 3, gap: '2px' }}>
        {actionable.map((s, i) => (
          <Tooltip key={s.asset} title={`${s.symbol}: ${formatUsd(s.dollar_allocation)} (${s.fib_label})`}>
            <Box
              sx={{
                height: '100%',
                width: `${(s.dollar_allocation / capital) * 100}%`,
                bgcolor: [COLORS.gold, COLORS.blue, '#9b59b6', COLORS.green, COLORS.amber][i] || COLORS.muted,
                transition: 'width 0.6s ease',
                cursor: 'pointer',
                '&:hover': { opacity: 0.85 },
              }}
            />
          </Tooltip>
        ))}
        {/* Cash reserve bar */}
        <Box
          sx={{
            height: '100%',
            flex: 1,
            bgcolor: COLORS.border,
          }}
        />
      </Box>

      {/* Allocation cards */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {actionable.map((s, i) => {
          const colors = [COLORS.gold, COLORS.blue, '#9b59b6', COLORS.green, COLORS.amber];
          const c = colors[i] || COLORS.muted;
          return (
            <Box
              key={s.asset}
              sx={{
                flex: '1 1 auto',
                minWidth: 80,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${c}40`,
                bgcolor: `${c}08`,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: c, fontSize: 10, letterSpacing: 1.5, mb: 0.5 }}>
                {s.fib_label?.split('(')[0]?.trim() || `P${i + 1}`}
              </Typography>
              <Typography sx={{ color: COLORS.platinum, fontSize: 15, fontWeight: 700 }}>
                {s.symbol}
              </Typography>
              <Typography sx={{ color: c, fontSize: 13, fontWeight: 700 }}>
                {formatUsd(s.dollar_allocation)}
              </Typography>
              <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
                {((s.dollar_allocation / capital) * 100).toFixed(1)}%
              </Typography>
            </Box>
          );
        })}

        {/* Cash */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 80,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.surface,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: COLORS.muted, fontSize: 10, letterSpacing: 1.5, mb: 0.5 }}>
            Reserve
          </Typography>
          <Typography sx={{ color: COLORS.platinum, fontSize: 15, fontWeight: 700 }}>
            CASH
          </Typography>
          <Typography sx={{ color: COLORS.platinum, fontSize: 13, fontWeight: 700 }}>
            {formatUsd(cashReserve)}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
            {((cashReserve / capital) * 100).toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Signal Card ─────────────────────────────────────────

function SignalCard({
  signal,
  rank,
  onSelect,
}: {
  signal: TomiSignal;
  rank: number;
  onSelect: (s: TomiSignal) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const actionColor = getActionColor(signal.action);
  const rankColors = [COLORS.gold, COLORS.blue, '#9b59b6', COLORS.green, COLORS.amber];
  const rankColor = signal.dollar_allocation > 0 ? rankColors[rank] || COLORS.muted : COLORS.muted;
  const isActionable = signal.action === 'BUY' || signal.action === 'LONG';

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${isActionable ? rankColor + '40' : COLORS.border}`,
        bgcolor: isActionable ? `${rankColor}06` : COLORS.surface,
        mb: 1.5,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': { borderColor: rankColor + '80' },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
        {/* Rank badge */}
        {isActionable && signal.dollar_allocation > 0 && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: `2px solid ${rankColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: rankColor, fontSize: 11, fontWeight: 800 }}>
              φ{rank + 1}
            </Typography>
          </Box>
        )}

        {/* Symbol */}
        <Box sx={{ minWidth: 48 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: 16 }}>
              {signal.symbol}
            </Typography>
            {signal.asset_class === 'equity' && (
              <Chip label="EQ" size="small" sx={{ bgcolor: `${COLORS.blue}20`, color: COLORS.blue, fontSize: 8, height: 16, '& .MuiChip-label': { px: 0.75 } }} />
            )}
          </Box>
          <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
            {signal.sector || signal.name}
          </Typography>
        </Box>

        {/* Action badge */}
        <Chip
          icon={getActionIcon(signal.action)}
          label={signal.action}
          size="small"
          sx={{
            bgcolor: `${actionColor}20`,
            color: actionColor,
            borderColor: `${actionColor}50`,
            border: '1px solid',
            fontWeight: 700,
            fontSize: 11,
            '& .MuiChip-icon': { color: actionColor },
          }}
        />

        {/* Conviction bars */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 9, minWidth: 28 }}>TOMI</Typography>
            {scoreBar(signal.dominion_score, COLORS.gold)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 9, minWidth: 28 }}>KAZE</Typography>
            {scoreBar(signal.kaze_score, COLORS.blue)}
          </Box>
        </Box>

        {/* Allocation */}
        {signal.dollar_allocation > 0 && (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ color: rankColor, fontWeight: 700, fontSize: 15 }}>
              {formatUsd(signal.dollar_allocation)}
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
              {signal.fib_label?.match(/\(([^)]+)\)/)?.[1] || ''}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {isActionable && (
            <Tooltip title="Execute guided trade">
              <IconButton
                size="small"
                onClick={() => onSelect(signal)}
                sx={{
                  bgcolor: `${actionColor}20`,
                  color: actionColor,
                  '&:hover': { bgcolor: `${actionColor}35` },
                  fontSize: 12,
                }}
              >
                <Send sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: COLORS.muted, '&:hover': { color: COLORS.platinum } }}
          >
            {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded detail */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${COLORS.border}` }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6} md={3}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>ENTRY</Typography>
              <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 13 }}>
                {formatPrice(signal.entry_price)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>STOP</Typography>
              <Typography sx={{ color: COLORS.red, fontWeight: 600, fontSize: 13 }}>
                {formatPrice(signal.stop_loss)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>TARGET</Typography>
              <Typography sx={{ color: COLORS.green, fontWeight: 600, fontSize: 13 }}>
                {formatPrice(signal.target)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>R:R</Typography>
              <Typography sx={{ color: signal.risk_reward >= 2 ? COLORS.green : COLORS.amber, fontWeight: 600, fontSize: 13 }}>
                {signal.risk_reward > 0 ? `${signal.risk_reward}:1` : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>KAZE INTELLIGENCE</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip label={signal.kaze_edge_type} size="small" sx={{ bgcolor: `${COLORS.blue}15`, color: COLORS.blue, fontSize: 10 }} />
                <Chip label={signal.kaze_consensus} size="small" sx={{ bgcolor: `${COLORS.gold}15`, color: COLORS.gold, fontSize: 10 }} />
                <Chip label={signal.kaze_catalyst} size="small" sx={{ bgcolor: `${COLORS.amber}15`, color: COLORS.amber, fontSize: 10 }} />
              </Box>
              {signal.kaze_narrative && (
                <Typography sx={{ color: COLORS.platinum, fontSize: 11, mt: 1, fontStyle: 'italic' }}>
                  {signal.kaze_narrative}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Guided Trade Dialog ─────────────────────────────────

function GuidedTradeDialog({
  signal,
  onClose,
  onConfirm,
  capital,
}: {
  signal: TomiSignal | null;
  onClose: () => void;
  onConfirm: (signal: TomiSignal, amount: string) => Promise<void>;
  capital: number;
}) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (signal) {
      setAmount(signal.dollar_allocation > 0 ? String(signal.dollar_allocation) : '10');
      setError(null);
    }
  }, [signal]);

  const handleConfirm = async () => {
    if (!signal) return;
    const amtNum = parseFloat(amount);
    if (isNaN(amtNum) || amtNum <= 0) {
      setError('Enter a valid dollar amount');
      return;
    }
    if (amtNum > capital) {
      setError(`Amount exceeds available capital (${formatUsd(capital)})`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(signal, amount);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!signal) return null;

  const actionColor = getActionColor(signal.action);
  const conviction = signal.combined_conviction * 100;

  return (
    <Dialog
      open={!!signal}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: COLORS.surface2,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: COLORS.platinum, borderBottom: `1px solid ${COLORS.border}`, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Psychology sx={{ color: COLORS.gold }} />
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.platinum, fontWeight: 700 }}>
              Tomi Guided Trade — {signal.symbol}
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              Review Tomi's verdict before executing
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Conviction gauge */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={100}
              sx={{ color: COLORS.border }}
            />
            <CircularProgress
              variant="determinate"
              value={conviction}
              size={100}
              sx={{
                color: conviction >= 70 ? COLORS.green : conviction >= 50 ? COLORS.amber : COLORS.red,
                position: 'absolute',
                left: 0,
              }}
            />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: COLORS.platinum, fontWeight: 800, fontSize: 18 }}>
                {conviction.toFixed(0)}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ color: COLORS.muted, fontSize: 11, letterSpacing: 2 }}>COMBINED CONVICTION</Typography>
        </Box>

        {/* Tomi verdict */}
        <Box sx={{
          p: 2, mb: 2, borderRadius: 2,
          border: `1px solid ${actionColor}40`,
          bgcolor: `${actionColor}08`,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          {getActionIcon(signal.action)}
          <Box>
            <Typography sx={{ color: actionColor, fontWeight: 800, fontSize: 15, letterSpacing: 1 }}>
              {signal.action} — {signal.name}
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
              Signal Class {signal.signal_class} · Dominion {(signal.dominion_score * 100).toFixed(0)}% · Kaze {(signal.kaze_score * 100).toFixed(0)}%
            </Typography>
          </Box>
        </Box>

        {/* Price levels */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Entry', value: formatPrice(signal.entry_price), color: COLORS.platinum },
            { label: 'Stop Loss', value: formatPrice(signal.stop_loss), color: COLORS.red },
            { label: 'Target', value: formatPrice(signal.target), color: COLORS.green },
            { label: 'Risk/Reward', value: signal.risk_reward > 0 ? `${signal.risk_reward}:1` : '—', color: signal.risk_reward >= 2 ? COLORS.green : COLORS.amber },
          ].map(item => (
            <Grid item xs={3} key={item.label}>
              <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: COLORS.surface, textAlign: 'center', border: `1px solid ${COLORS.border}` }}>
                <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>{item.label.toUpperCase()}</Typography>
                <Typography sx={{ color: item.color, fontWeight: 700, fontSize: 12 }}>{item.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Kaze context */}
        <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: `${COLORS.blue}08`, border: `1px solid ${COLORS.blue}20` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Air sx={{ fontSize: 14, color: COLORS.blue }} />
            <Typography sx={{ color: COLORS.blue, fontSize: 11, fontWeight: 600, letterSpacing: 1 }}>KAZE INTELLIGENCE</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
            <Chip label={signal.kaze_edge_type} size="small" sx={{ bgcolor: `${COLORS.blue}20`, color: COLORS.blue, fontSize: 10 }} />
            <Chip label={signal.kaze_consensus} size="small" sx={{ bgcolor: `${COLORS.gold}20`, color: COLORS.gold, fontSize: 10 }} />
            <Chip label={signal.kaze_catalyst} size="small" sx={{ bgcolor: `${COLORS.amber}20`, color: COLORS.amber, fontSize: 10 }} />
          </Box>
          {signal.kaze_narrative && (
            <Typography sx={{ color: COLORS.platinum, fontSize: 11, fontStyle: 'italic' }}>
              {signal.kaze_narrative}
            </Typography>
          )}
        </Box>

        {/* Fibonacci allocation recommendation */}
        {signal.dollar_allocation > 0 && (
          <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: `${COLORS.gold}06`, border: `1px solid ${COLORS.gold}25` }}>
            <Typography sx={{ color: COLORS.gold, fontSize: 11, fontWeight: 600, letterSpacing: 1, mb: 0.5 }}>
              φ FIBONACCI RECOMMENDATION
            </Typography>
            <Typography sx={{ color: COLORS.platinum, fontSize: 12 }}>
              Tomi allocates <strong style={{ color: COLORS.gold }}>{formatUsd(signal.dollar_allocation)}</strong> ({signal.fib_label}) based on conviction rank and Fibonacci sequencing.
            </Typography>
          </Box>
        )}

        {/* Amount input */}
        <TextField
          fullWidth
          label="Trade Amount (USD)"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          type="number"
          inputProps={{ min: 1, max: capital, step: 1 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: COLORS.platinum,
              '& fieldset': { borderColor: COLORS.border },
              '&:hover fieldset': { borderColor: COLORS.gold },
              '&.Mui-focused fieldset': { borderColor: COLORS.gold },
            },
            '& .MuiInputLabel-root': { color: COLORS.muted },
            '& .MuiInputLabel-root.Mui-focused': { color: COLORS.gold },
          }}
          helperText={
            <Typography component="span" sx={{ color: COLORS.muted, fontSize: 10 }}>
              Tomi's Fibonacci recommendation: {formatUsd(signal.dollar_allocation)} ·
              Available: {formatUsd(capital)}
            </Typography>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2, bgcolor: `${COLORS.red}15`, color: COLORS.red, border: `1px solid ${COLORS.red}30` }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${COLORS.border}`, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: COLORS.muted, '&:hover': { color: COLORS.platinum } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting}
          variant="contained"
          sx={{
            bgcolor: actionColor,
            color: '#000',
            fontWeight: 700,
            px: 3,
            '&:hover': { bgcolor: actionColor, opacity: 0.9 },
            '&.Mui-disabled': { bgcolor: `${actionColor}40` },
          }}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} sx={{ color: '#000' }} />
              Executing...
            </Box>
          ) : (
            `Execute ${signal.action} — ${formatUsd(parseFloat(amount) || 0)}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Learning Panel ──────────────────────────────────────

function LearningPanel({ stats }: { stats: LearningStats | null }) {
  if (!stats) return null;

  const hasActive = (stats.active_positions ?? 0) > 0;
  const hasData = stats.total_signals > 0 || hasActive;

  return (
    <Box>
      <Typography variant="caption" sx={{ color: COLORS.muted, letterSpacing: 2, textTransform: 'uppercase', display: 'block', mb: 2 }}>
        Tomi Learning Engine
      </Typography>

      <Grid container spacing={1.5}>
        {[
          {
            label: 'Signals Tracked',
            value: stats.total_signals,
            color: COLORS.platinum,
            icon: <Timeline sx={{ fontSize: 16 }} />,
          },
          {
            label: 'Active Positions',
            value: hasActive ? stats.active_positions : 0,
            color: hasActive ? COLORS.green : COLORS.muted,
            icon: <RadioButtonChecked sx={{ fontSize: 16 }} />,
          },
          {
            label: 'Win Rate',
            value: hasData && stats.win_rate !== null ? `${stats.win_rate}%` : 'N/A',
            color: hasData && stats.win_rate !== null && stats.win_rate >= 60 ? COLORS.green : COLORS.amber,
            icon: <EmojiEvents sx={{ fontSize: 16 }} />,
          },
          {
            label: 'Unrealized P&L',
            value: stats.unrealized_pl !== undefined ? `${stats.unrealized_pl >= 0 ? '+' : ''}$${Math.abs(stats.unrealized_pl).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : 'N/A',
            color: (stats.unrealized_pl ?? 0) >= 0 ? COLORS.green : COLORS.red,
            icon: <TrendingUp sx={{ fontSize: 16 }} />,
          },
        ].map(item => (
          <Grid item xs={6} key={item.label}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, color: COLORS.muted }}>
                {item.icon}
                <Typography sx={{ fontSize: 9, letterSpacing: 1.5, color: COLORS.muted }}>{item.label.toUpperCase()}</Typography>
              </Box>
              <Typography sx={{ color: item.color, fontWeight: 700, fontSize: 18 }}>
                {item.value}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Per-asset breakdown */}
      {hasData && Object.keys(stats.by_asset).length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: COLORS.muted, fontSize: 10, letterSpacing: 1.5, mb: 1 }}>BY ASSET</Typography>
          {Object.entries(stats.by_asset).map(([asset, data]) => (
            <Box key={asset} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 11, minWidth: 40 }}>{asset}</Typography>
              <LinearProgress
                variant="determinate"
                value={data.total > 0 ? (data.wins / data.total) * 100 : 0}
                sx={{
                  flex: 1,
                  height: 5,
                  borderRadius: 3,
                  bgcolor: `${COLORS.red}30`,
                  '& .MuiLinearProgress-bar': { bgcolor: COLORS.green, borderRadius: 3 },
                }}
              />
              <Typography sx={{ color: COLORS.muted, fontSize: 10, minWidth: 40 }}>
                {data.wins}/{data.total}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {!hasData && (
        <Box sx={{ mt: 2, textAlign: 'center', py: 2 }}>
          <Insights sx={{ color: COLORS.muted, fontSize: 28, mb: 1, display: 'block', mx: 'auto' }} />
          <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
            Execute signals to begin tracking outcomes.
          </Typography>
        </Box>
      )}

      {/* Active positions live P&L */}
      {hasActive && stats.active_signals && stats.active_signals.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: COLORS.muted, fontSize: 10, letterSpacing: 1.5, mb: 1 }}>LIVE POSITIONS</Typography>
          {stats.active_signals.map((sig: any) => {
            const pl = sig.unrealized_pl ?? 0;
            const plPct = sig.pnl_pct ?? 0;
            const isUp = pl >= 0;
            return (
              <Box key={sig.asset} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, p: 1, borderRadius: 1.5, bgcolor: `${isUp ? COLORS.green : COLORS.red}10` }}>
                <Typography sx={{ color: COLORS.platinum, fontSize: 11, fontWeight: 600, minWidth: 100, fontFamily: 'monospace' }}>{sig.asset}</Typography>
                <Typography sx={{ color: isUp ? COLORS.green : COLORS.red, fontSize: 11, fontWeight: 700, ml: 'auto' }}>
                  {isUp ? '+' : ''}{pl.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                </Typography>
                <Typography sx={{ color: isUp ? COLORS.green : COLORS.red, fontSize: 10, minWidth: 52, textAlign: 'right' }}>
                  {isUp ? '+' : ''}{plPct.toFixed(1)}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ─── Equity Portfolio Panel ──────────────────────────────

function AlpacaPortfolioPanel({
  positions,
  loading,
}: {
  positions: { positions: AlpacaPosition[]; count: number; total_market_value: number; total_unrealized_pl: number; buying_power: string } | null;
  loading: boolean;
}) {
  return (
    <Box sx={{ p: 2.5, mb: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ShowChart sx={{ color: COLORS.blue, fontSize: 18 }} />
        <Typography sx={{ color: COLORS.muted, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>
          Alpaca Equity Portfolio
        </Typography>
      </Box>

      {loading && <CircularProgress size={24} sx={{ color: COLORS.blue, display: 'block', mx: 'auto', my: 2 }} />}

      {!loading && !positions && (
        <Typography sx={{ color: COLORS.muted, fontSize: 12, textAlign: 'center', py: 2 }}>
          Configure ALPACA_API_KEY to enable equity trading
        </Typography>
      )}

      {!loading && positions && (
        <>
          {/* Summary row */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>POSITIONS</Typography>
              <Typography sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: 16 }}>{positions.count}</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>MKT VALUE</Typography>
              <Typography sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: 14 }}>{formatUsd(positions.total_market_value)}</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>UNREAL P&L</Typography>
              <Typography sx={{
                fontWeight: 700, fontSize: 14,
                color: positions.total_unrealized_pl >= 0 ? COLORS.green : COLORS.red,
              }}>
                {positions.total_unrealized_pl >= 0 ? '+' : ''}{formatUsd(positions.total_unrealized_pl)}
              </Typography>
            </Box>
          </Box>

          {positions.positions.length === 0 ? (
            <Typography sx={{ color: COLORS.muted, fontSize: 12, textAlign: 'center', py: 2 }}>
              No equity positions · Buying power: {formatUsd(parseFloat(positions.buying_power))}
            </Typography>
          ) : (
            <Box>
              {positions.positions.map(p => {
                const pl = parseFloat(p.unrealized_pl || '0');
                const plPct = parseFloat(p.unrealized_plpc || '0') * 100;
                return (
                  <Box
                    key={p.asset_id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.25,
                      borderBottom: `1px solid ${COLORS.border}`,
                      '&:last-child': { borderBottom: 0 },
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 13 }}>{p.symbol}</Typography>
                      <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
                        {parseFloat(p.qty).toFixed(4)} shares @ {formatUsd(parseFloat(p.avg_entry_price))}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 13 }}>
                        {formatUsd(parseFloat(p.market_value))}
                      </Typography>
                      <Typography sx={{ color: pl >= 0 ? COLORS.green : COLORS.red, fontSize: 10 }}>
                        {pl >= 0 ? '+' : ''}{formatUsd(pl)} ({plPct.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>Buying Power</Typography>
            <Typography sx={{ color: COLORS.blue, fontWeight: 600, fontSize: 11 }}>
              {formatUsd(parseFloat(positions.buying_power))}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

// ─── Cross-market signal merge ───────────────────────────

const FIB_ALLOC_ARR = [0.382, 0.236, 0.146, 0.090, 0.056];
const FIB_LABEL_ARR = ['Primary (38.2%)', 'Secondary (23.6%)', 'Tertiary (14.6%)', 'Quarter (9.0%)', 'Sentinel (5.6%)'];

function mergeSignals(cryptoData: SignalsData, equityData: SignalsData, capital: number): SignalsData {
  const all = [...cryptoData.signals, ...equityData.signals]
    .sort((a, b) => b.combined_conviction - a.combined_conviction);

  const buys = all.filter(s => s.action === 'BUY' || s.action === 'LONG');
  const others = all.filter(s => s.action !== 'BUY' && s.action !== 'LONG');

  const allocBuys = buys.map((s, i) =>
    i < FIB_ALLOC_ARR.length
      ? { ...s, fib_ratio: FIB_ALLOC_ARR[i], fib_label: FIB_LABEL_ARR[i], dollar_allocation: +(capital * FIB_ALLOC_ARR[i]).toFixed(2) }
      : { ...s, fib_label: 'Unallocated', dollar_allocation: 0 }
  );

  const merged = [...allocBuys, ...others.map(s => ({ ...s, fib_label: 'Abstain', dollar_allocation: 0 }))];
  const totalAllocated = allocBuys.slice(0, FIB_ALLOC_ARR.length).reduce((sum, s) => sum + s.dollar_allocation, 0);

  return {
    signals: merged,
    capital,
    total_allocated: +totalAllocated.toFixed(2),
    cash_reserve: +(capital - totalAllocated).toFixed(2),
    fib_ratios: FIB_ALLOC_ARR,
    kaze_connected: cryptoData.kaze_connected || equityData.kaze_connected,
    tomi_evaluated: cryptoData.tomi_evaluated + equityData.tomi_evaluated,
    generated_at: new Date().toISOString(),
  };
}

// ─── Options Signal Card ─────────────────────────────────

function OptionsSignalCard({
  signal,
  rank,
  onSelect,
}: {
  signal: OptionsSignal;
  rank: number;
  onSelect: (s: OptionsSignal) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const rankColors = [COLORS.gold, COLORS.blue, '#9b59b6', COLORS.green, COLORS.amber];
  const rankColor = signal.dollar_allocation > 0 ? rankColors[rank] || COLORS.muted : COLORS.muted;
  const isActionable = signal.has_options && signal.contracts > 0;
  const actionColor = isActionable ? COLORS.green : COLORS.muted;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${isActionable ? rankColor + '40' : COLORS.border}`,
        bgcolor: isActionable ? `${rankColor}06` : COLORS.surface,
        mb: 1.5,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': { borderColor: rankColor + '80' },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
        {/* Rank badge */}
        {isActionable && (
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${rankColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ color: rankColor, fontSize: 11, fontWeight: 800 }}>φ{rank + 1}</Typography>
          </Box>
        )}

        {/* Symbol */}
        <Box sx={{ minWidth: 64 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: 16 }}>{signal.symbol}</Typography>
            <Chip label="OPT" size="small" sx={{ bgcolor: `#9b59b620`, color: '#9b59b6', fontSize: 8, height: 16, '& .MuiChip-label': { px: 0.75 } }} />
          </Box>
          <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
            {signal.sector || signal.name}
          </Typography>
        </Box>

        {/* Contract info */}
        {signal.has_options && signal.contract_symbol ? (
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: COLORS.platinum, fontSize: 11, fontWeight: 600, fontFamily: 'monospace' }}>
              {signal.contract_symbol}
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
              ${signal.strike} call · {signal.expiry} · {signal.dte}d
              {signal.delta != null ? ` · Δ${signal.delta.toFixed(2)}` : ''}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>No listed options found</Typography>
          </Box>
        )}

        {/* Premium / contracts */}
        {isActionable && (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ color: rankColor, fontWeight: 700, fontSize: 15 }}>
              {signal.contracts}× @ {formatUsd(signal.ask_price)}
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
              {formatUsd(signal.total_premium)} total premium
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {isActionable && (
            <Tooltip title="Execute options order">
              <IconButton
                size="small"
                onClick={() => onSelect(signal)}
                sx={{ bgcolor: `${actionColor}20`, color: actionColor, '&:hover': { bgcolor: `${actionColor}35` } }}
              >
                <Send sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ color: COLORS.muted, '&:hover': { color: COLORS.platinum } }}>
            {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded detail */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${COLORS.border}` }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Conviction bars */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 1 }}>CONVICTION</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography sx={{ color: COLORS.muted, fontSize: 9, minWidth: 36 }}>TOMI</Typography>
                {scoreBar(signal.dominion_score, COLORS.gold)}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: COLORS.muted, fontSize: 9, minWidth: 36 }}>KAZE</Typography>
                {scoreBar(signal.kaze_score, COLORS.blue)}
              </Box>
            </Grid>
            {/* Options levels */}
            {signal.has_options && (
              <>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>STOCK ENTRY</Typography>
                  <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 13 }}>{formatPrice(signal.entry_price)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>STOCK TARGET</Typography>
                  <Typography sx={{ color: COLORS.green, fontWeight: 600, fontSize: 13 }}>{formatPrice(signal.target)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>ASK (PER SHARE)</Typography>
                  <Typography sx={{ color: COLORS.gold, fontWeight: 600, fontSize: 13 }}>{formatUsd(signal.ask_price)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>COST/CONTRACT</Typography>
                  <Typography sx={{ color: COLORS.gold, fontWeight: 600, fontSize: 13 }}>{formatUsd(signal.contract_cost)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>STOP (−50%)</Typography>
                  <Typography sx={{ color: COLORS.red, fontWeight: 600, fontSize: 13 }}>{formatUsd(signal.stop_premium)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>TARGET 1× (+100%)</Typography>
                  <Typography sx={{ color: COLORS.green, fontWeight: 600, fontSize: 13 }}>{formatUsd(signal.target_1x)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>TARGET 2× (+200%)</Typography>
                  <Typography sx={{ color: COLORS.green, fontWeight: 600, fontSize: 13 }}>{formatUsd(signal.target_2x)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.5 }}>IMPLIED VOL</Typography>
                  <Typography sx={{ color: COLORS.amber, fontWeight: 600, fontSize: 13 }}>
                    {signal.implied_vol != null ? `${(signal.implied_vol * 100).toFixed(1)}%` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip label={signal.kaze_edge_type || 'options_momentum'} size="small" sx={{ bgcolor: `${COLORS.blue}15`, color: COLORS.blue, fontSize: 10 }} />
                    <Chip label={signal.fib_label || 'Unallocated'} size="small" sx={{ bgcolor: `${COLORS.gold}15`, color: COLORS.gold, fontSize: 10 }} />
                    <Chip label={`Fib ${formatUsd(signal.dollar_allocation)}`} size="small" sx={{ bgcolor: `${COLORS.gold}15`, color: COLORS.gold, fontSize: 10 }} />
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Options Trade Dialog ────────────────────────────────

function OptionsTradeDialog({
  signal,
  onClose,
  onConfirm,
}: {
  signal: OptionsSignal | null;
  onClose: () => void;
  onConfirm: (signal: OptionsSignal) => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!signal) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(signal);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!signal) return null;
  const conviction = signal.combined_conviction * 100;

  return (
    <Dialog
      open={!!signal}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: COLORS.surface2, border: `1px solid ${COLORS.border}`, borderRadius: 3 } }}
    >
      <DialogTitle sx={{ color: COLORS.platinum, borderBottom: `1px solid ${COLORS.border}`, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Analytics sx={{ color: '#9b59b6' }} />
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.platinum, fontWeight: 700 }}>
              Tomi Options Order — {signal.symbol}
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {signal.contract_symbol} · {signal.contracts} contract{signal.contracts !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Conviction gauge */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
            <CircularProgress variant="determinate" value={100} size={100} sx={{ color: COLORS.border }} />
            <CircularProgress variant="determinate" value={conviction} size={100} sx={{
              color: conviction >= 70 ? COLORS.green : conviction >= 50 ? COLORS.amber : COLORS.red,
              position: 'absolute', left: 0,
            }} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: COLORS.platinum, fontWeight: 800, fontSize: 18 }}>{conviction.toFixed(0)}</Typography>
            </Box>
          </Box>
          <Typography sx={{ color: COLORS.muted, fontSize: 11, letterSpacing: 2 }}>COMBINED CONVICTION</Typography>
        </Box>

        {/* Contract detail */}
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${COLORS.gold}30`, bgcolor: `${COLORS.gold}06` }}>
          <Typography sx={{ color: COLORS.gold, fontWeight: 800, fontSize: 14, letterSpacing: 1, mb: 1 }}>
            BUY {signal.contracts} × {signal.contract_symbol}
          </Typography>
          <Grid container spacing={1}>
            {[
              { label: 'Strike', value: formatPrice(signal.strike) },
              { label: 'Expiry', value: signal.expiry },
              { label: 'DTE', value: `${signal.dte}d` },
              { label: 'Ask', value: formatUsd(signal.ask_price) },
              { label: 'Per Contract', value: formatUsd(signal.contract_cost) },
              { label: 'Delta', value: signal.delta != null ? signal.delta.toFixed(2) : '—' },
              { label: 'IV', value: signal.implied_vol != null ? `${(signal.implied_vol * 100).toFixed(1)}%` : '—' },
              { label: 'Total Premium', value: formatUsd(signal.total_premium) },
            ].map(item => (
              <Grid item xs={6} key={item.label}>
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: COLORS.surface, textAlign: 'center', border: `1px solid ${COLORS.border}` }}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>{item.label.toUpperCase()}</Typography>
                  <Typography sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: 12 }}>{item.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Risk levels */}
        <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: `${COLORS.blue}06`, border: `1px solid ${COLORS.blue}20` }}>
          <Typography sx={{ color: COLORS.blue, fontSize: 11, fontWeight: 600, letterSpacing: 1, mb: 1 }}>RISK LEVELS (PER CONTRACT)</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9 }}>STOP −50%</Typography>
              <Typography sx={{ color: COLORS.red, fontWeight: 700, fontSize: 12 }}>{formatUsd(signal.stop_premium)}</Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9 }}>TARGET 1×</Typography>
              <Typography sx={{ color: COLORS.green, fontWeight: 700, fontSize: 12 }}>{formatUsd(signal.target_1x)}</Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9 }}>TARGET 2×</Typography>
              <Typography sx={{ color: COLORS.green, fontWeight: 700, fontSize: 12 }}>{formatUsd(signal.target_2x)}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Fib allocation context */}
        <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: `${COLORS.gold}06`, border: `1px solid ${COLORS.gold}25` }}>
          <Typography sx={{ color: COLORS.gold, fontSize: 11, fontWeight: 600, letterSpacing: 1, mb: 0.5 }}>φ FIBONACCI RECOMMENDATION</Typography>
          <Typography sx={{ color: COLORS.platinum, fontSize: 12 }}>
            Budget: <strong style={{ color: COLORS.gold }}>{formatUsd(signal.dollar_allocation)}</strong> ({signal.fib_label}) →{' '}
            <strong style={{ color: COLORS.green }}>{signal.contracts} contracts</strong> @ {formatUsd(signal.ask_price)}/share ={' '}
            <strong style={{ color: COLORS.gold }}>{formatUsd(signal.total_premium)}</strong> total premium
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, bgcolor: `${COLORS.red}15`, color: COLORS.red, border: `1px solid ${COLORS.red}30` }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${COLORS.border}`, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: COLORS.muted, '&:hover': { color: COLORS.platinum } }}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting || signal.contracts === 0}
          variant="contained"
          sx={{ bgcolor: COLORS.green, color: '#000', fontWeight: 700, px: 3, '&:hover': { bgcolor: COLORS.green, opacity: 0.9 }, '&.Mui-disabled': { bgcolor: `${COLORS.green}40` } }}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} sx={{ color: '#000' }} />
              Executing...
            </Box>
          ) : (
            `Buy ${signal.contracts} Contract${signal.contracts !== 1 ? 's' : ''} — ${formatUsd(signal.total_premium)}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Options Positions Panel ─────────────────────────────

function OptionsPositionsPanel({
  positions,
  loading,
}: {
  positions: { positions: AlpacaPosition[]; count: number; total_market_value: number; total_unrealized_pl: number } | null;
  loading: boolean;
}) {
  return (
    <Box sx={{ p: 2.5, mb: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Analytics sx={{ color: '#9b59b6', fontSize: 18 }} />
        <Typography sx={{ color: COLORS.muted, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>
          Options Positions
        </Typography>
      </Box>

      {loading && <CircularProgress size={24} sx={{ color: '#9b59b6', display: 'block', mx: 'auto', my: 2 }} />}

      {!loading && (!positions || positions.count === 0) && (
        <Typography sx={{ color: COLORS.muted, fontSize: 12, textAlign: 'center', py: 2 }}>
          No options positions open
        </Typography>
      )}

      {!loading && positions && positions.count > 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>CONTRACTS</Typography>
              <Typography sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: 16 }}>{positions.count}</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>MKT VALUE</Typography>
              <Typography sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: 14 }}>{formatUsd(positions.total_market_value)}</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, textAlign: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>UNREAL P&L</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: positions.total_unrealized_pl >= 0 ? COLORS.green : COLORS.red }}>
                {positions.total_unrealized_pl >= 0 ? '+' : ''}{formatUsd(positions.total_unrealized_pl)}
              </Typography>
            </Box>
          </Box>
          {positions.positions.map(p => {
            const pl = parseFloat(p.unrealized_pl || '0');
            const plPct = parseFloat(p.unrealized_plpc || '0') * 100;
            return (
              <Box key={p.asset_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25, borderBottom: `1px solid ${COLORS.border}`, '&:last-child': { borderBottom: 0 } }}>
                <Box>
                  <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 12, fontFamily: 'monospace' }}>{p.symbol}</Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
                    {parseFloat(p.qty).toFixed(0)} contracts @ {formatUsd(parseFloat(p.avg_entry_price))}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 13 }}>{formatUsd(parseFloat(p.market_value))}</Typography>
                  <Typography sx={{ color: pl >= 0 ? COLORS.green : COLORS.red, fontSize: 10 }}>
                    {pl >= 0 ? '+' : ''}{formatUsd(pl)} ({plPct.toFixed(1)}%)
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </>
      )}
    </Box>
  );
}

// ─── Autopilot Panel ─────────────────────────────────────

const DECISION_ICONS: Record<string, React.ReactNode> = {
  enter: <CheckCircle sx={{ fontSize: 13, color: '#4caf50' }} />,
  exit: <Stop sx={{ fontSize: 13, color: '#f44336' }} />,
  skip: <SkipNext sx={{ fontSize: 13, color: '#9e9e9e' }} />,
  monitor: <MonitorHeart sx={{ fontSize: 13, color: '#4a7fff' }} />,
  scan: <RadioButtonChecked sx={{ fontSize: 13, color: '#c8951a' }} />,
  error: <ErrorIcon sx={{ fontSize: 13, color: '#f44336' }} />,
  posture: <Psychology sx={{ fontSize: 13, color: '#c8951a' }} />,
};

const POSTURE_COLORS: Record<string, string> = {
  offensive: '#4caf50',
  neutral:   '#c8951a',
  defensive: '#ff9800',
  preservation: '#f44336',
};

function AutopilotPanel({
  session,
  running,
  portfolio,
  onLaunch,
  onStop,
  launching,
}: {
  session: AutopilotSession | null;
  running: boolean;
  portfolio: { cash: number; portfolio_value: number; positions: number; market_value: number; unrealized_pl: number } | null;
  onLaunch: (mode: AutopilotMode) => void;
  onStop: () => void;
  launching: boolean;
}) {
  const [mode, setMode] = useState<AutopilotMode>('standard');
  const isRunning = running || session?.status === 'running';

  const modeConfig = {
    conservative: { label: 'Conservative', color: '#4a7fff', desc: '3 pos · 30min · 0.72+ DS' },
    standard:     { label: 'Standard',     color: '#c8951a', desc: '5 pos · 20min · 0.65+ DS' },
    aggressive:   { label: 'Aggressive',   color: '#f44336', desc: '8 pos · 10min · 0.55+ DS' },
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Psychology sx={{ color: isRunning ? '#4caf50' : COLORS.gold, fontSize: 18 }} />
          <Typography variant="caption" sx={{ color: COLORS.muted, letterSpacing: 2, textTransform: 'uppercase' }}>
            Tomi Autopilot
          </Typography>
          {isRunning && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4caf50', animation: 'pulse 1.5s infinite' }} />
              <Typography sx={{ color: '#4caf50', fontSize: 10, fontWeight: 700 }}>LIVE</Typography>
            </Box>
          )}
        </Box>
        {isRunning && (
          <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
            Cycle #{session?.cycles} · {session?.stats.entries} entries
          </Typography>
        )}
      </Box>

      {/* Portfolio snapshot */}
      {portfolio && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
          {[
            { label: 'Cash', value: `$${portfolio.cash.toLocaleString('en-US', { maximumFractionDigits: 0 })}` },
            { label: 'Portfolio', value: `$${portfolio.portfolio_value.toLocaleString('en-US', { maximumFractionDigits: 0 })}` },
            { label: 'Deployed', value: `$${portfolio.market_value.toLocaleString('en-US', { maximumFractionDigits: 0 })}` },
            { label: 'Unrealized P&L', value: `${portfolio.unrealized_pl >= 0 ? '+' : ''}$${portfolio.unrealized_pl.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, color: portfolio.unrealized_pl >= 0 ? '#4caf50' : '#f44336' },
          ].map(item => (
            <Box key={item.label} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: COLORS.surface2, border: `1px solid ${COLORS.border}` }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5, mb: 0.25 }}>{item.label.toUpperCase()}</Typography>
              <Typography sx={{ color: item.color || COLORS.platinum, fontWeight: 700, fontSize: 13 }}>{item.value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {!isRunning ? (
        <>
          {/* Mode selector */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 10, letterSpacing: 1.5, mb: 1 }}>SELECT MODE</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {(['conservative', 'standard', 'aggressive'] as AutopilotMode[]).map(m => (
                <Box
                  key={m}
                  onClick={() => setMode(m)}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: `1px solid ${mode === m ? modeConfig[m].color : COLORS.border}`,
                    bgcolor: mode === m ? `${modeConfig[m].color}12` : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.15s',
                  }}
                >
                  <Typography sx={{ color: mode === m ? modeConfig[m].color : COLORS.muted, fontSize: 12, fontWeight: mode === m ? 700 : 400, textTransform: 'capitalize' }}>
                    {modeConfig[m].label}
                  </Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>{modeConfig[m].desc}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={launching ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
            disabled={launching}
            onClick={() => onLaunch(mode)}
            sx={{
              bgcolor: COLORS.gold,
              color: '#000',
              fontWeight: 700,
              letterSpacing: 1,
              py: 1.25,
              '&:hover': { bgcolor: COLORS.goldLight },
              '&:disabled': { bgcolor: COLORS.goldDim, color: COLORS.muted },
            }}
          >
            {launching ? 'Launching...' : 'Launch Tomi'}
          </Button>
          <Typography sx={{ color: COLORS.muted, fontSize: 10, mt: 1, textAlign: 'center' }}>
            Tomi will autonomously scan, evaluate, and execute trades using the full org intelligence stack.
          </Typography>
        </>
      ) : (
        <>
          {/* Stratagem Nexus posture */}
          {session?.posture && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: `${POSTURE_COLORS[session.posture.regime] || COLORS.gold}15`, border: `1px solid ${POSTURE_COLORS[session.posture.regime] || COLORS.gold}40` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Psychology sx={{ fontSize: 13, color: POSTURE_COLORS[session.posture.regime] || COLORS.gold }} />
                <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>STRATAGEM NEXUS</Typography>
                <Typography sx={{ color: POSTURE_COLORS[session.posture.regime] || COLORS.gold, fontSize: 10, fontWeight: 700, ml: 'auto', textTransform: 'uppercase' }}>
                  {session.posture.regime}
                </Typography>
              </Box>
              <Typography sx={{ color: COLORS.muted, fontSize: 10, lineHeight: 1.4 }}>{session.posture.reason}</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.75 }}>
                <Typography sx={{ color: COLORS.muted, fontSize: 9 }}>Deploy: {(session.posture.max_deploy_pct * 100).toFixed(0)}%</Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 9 }}>Max pos: {session.posture.max_positions}</Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 9 }}>Min DS: {session.posture.min_conviction.toFixed(2)}</Typography>
                {session.posture.short_bias && <Typography sx={{ color: '#ff9800', fontSize: 9, fontWeight: 700 }}>SHORT BIAS</Typography>}
              </Box>
            </Box>
          )}

          {/* Running stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0.75, mb: 2 }}>
            {[
              { label: 'Entries', value: session?.stats.entries ?? 0, color: '#4caf50' },
              { label: 'Exits', value: session?.stats.exits ?? 0, color: '#e57373' },
              { label: 'Wins', value: session?.stats.wins ?? 0, color: '#4caf50' },
              { label: 'Losses', value: session?.stats.losses ?? 0, color: '#f44336' },
            ].map(s => (
              <Box key={s.label} sx={{ p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, border: `1px solid ${COLORS.border}`, textAlign: 'center' }}>
                <Typography sx={{ color: s.color, fontWeight: 700, fontSize: 15 }}>{s.value}</Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 9, letterSpacing: 1.5 }}>{s.label.toUpperCase()}</Typography>
              </Box>
            ))}
          </Box>

          {/* Realized P&L */}
          {(session?.stats.realized_pl ?? 0) !== 0 && (
            <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: COLORS.surface2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>Realized P&L</Typography>
              <Typography sx={{ color: (session?.stats.realized_pl ?? 0) >= 0 ? '#4caf50' : '#f44336', fontWeight: 700, fontSize: 12 }}>
                {(session?.stats.realized_pl ?? 0) >= 0 ? '+' : ''}{(session?.stats.realized_pl ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
              </Typography>
            </Box>
          )}

          {session?.next_scan && (
            <Typography sx={{ color: COLORS.muted, fontSize: 10, mb: 1.5, textAlign: 'center' }}>
              Next scan: {new Date(session.next_scan).toLocaleTimeString()} · Cycle #{session?.cycles}
            </Typography>
          )}

          {/* Recent decisions */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 10, letterSpacing: 1.5, mb: 1 }}>RECENT DECISIONS</Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {(session?.decisions || []).slice(0, 15).map((d: AutopilotDecision) => (
                <Box key={d.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, p: 0.75, borderRadius: 1, bgcolor: COLORS.surface2 }}>
                  {DECISION_ICONS[d.type] || <RadioButtonChecked sx={{ fontSize: 13, color: COLORS.muted }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Typography sx={{ color: COLORS.platinum, fontSize: 11, fontWeight: 600 }}>{d.symbol}</Typography>
                      <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>{d.action}</Typography>
                    </Box>
                    <Typography sx={{ color: COLORS.muted, fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.reason}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9, whiteSpace: 'nowrap' }}>
                    {new Date(d.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              ))}
              {(!session?.decisions || session.decisions.length === 0) && (
                <Typography sx={{ color: COLORS.muted, fontSize: 11, textAlign: 'center', py: 2 }}>
                  Scanning market...
                </Typography>
              )}
            </Box>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<Stop />}
            onClick={onStop}
            sx={{ borderColor: '#f44336', color: '#f44336', fontWeight: 700, '&:hover': { bgcolor: '#f4433610', borderColor: '#f44336' } }}
          >
            Stop Autopilot
          </Button>
        </>
      )}
    </Box>
  );
}

// ─── Main Command Center ─────────────────────────────────

export default function TomiCommandCenterPage() {
  const [signalsData, setSignalsData] = useState<SignalsData | null>(null);
  const [portfolio, setPortfolio] = useState<SpotPortfolio | null>(null);
  const [equityPortfolio, setEquityPortfolio] = useState<{ positions: AlpacaPosition[]; count: number; total_market_value: number; total_unrealized_pl: number; buying_power: string } | null>(null);
  const [optionsPositions, setOptionsPositions] = useState<{ positions: AlpacaPosition[]; count: number; total_market_value: number; total_unrealized_pl: number } | null>(null);
  const [learningStats, setLearningStats] = useState<LearningStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [capital, setCapital] = useState(200);
  const [market, setMarket] = useState<'crypto' | 'equity' | 'all' | 'options'>('crypto');
  const [selectedSignal, setSelectedSignal] = useState<TomiSignal | null>(null);
  const [selectedOptionsSignal, setSelectedOptionsSignal] = useState<OptionsSignal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);
  const [autopilotSession, setAutopilotSession] = useState<AutopilotSession | null>(null);
  const [autopilotRunning, setAutopilotRunning] = useState(false);
  const [autopilotPortfolio, setAutopilotPortfolio] = useState<{ cash: number; portfolio_value: number; positions: number; market_value: number; unrealized_pl: number } | null>(null);
  const [autopilotLaunching, setAutopilotLaunching] = useState(false);

  // Generate signals (market-aware)
  const generateSignals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data: SignalsData;
      if (market === 'crypto') {
        data = await tomiService.getSignals(capital);
      } else if (market === 'equity') {
        data = await tomiService.getEquitySignals(capital);
      } else if (market === 'options') {
        // Options uses full $100k paper balance
        data = await tomiService.getOptionsSignals(capital);
      } else {
        // 'all' — fetch both, re-apply Fibonacci cross-market
        const [cryptoData, equityData] = await Promise.all([
          tomiService.getSignals(capital),
          tomiService.getEquitySignals(capital),
        ]);
        data = mergeSignals(cryptoData, equityData, capital);
      }
      setSignalsData(data);
      setLastGenerated(new Date().toLocaleTimeString());
    } catch (err: any) {
      setError('Signal generation failed — verify Kaze and Tomi services are running');
    } finally {
      setLoading(false);
    }
  }, [capital, market]);

  // Load portfolio + equity positions + options positions + learning stats
  const loadPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const [p, ls, eq, opt] = await Promise.all([
        tomiService.getPortfolio().catch(() => null),
        tomiService.getLearningStats().catch(() => null),
        tomiService.getEquityPositions().catch(() => null),
        tomiService.getOptionsPositions().catch(() => null),
      ]);
      if (p) setPortfolio(p);
      if (ls) setLearningStats(ls);
      if (eq) setEquityPortfolio(eq);
      if (opt) setOptionsPositions(opt);
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  const loadAutopilotStatus = useCallback(async () => {
    try {
      const status = await tomiService.getAutopilotStatus();
      setAutopilotSession(status.session);
      // Use the explicit `running` boolean from the API — more reliable than session?.status
      // which can be null when the backend restarted but a session is being resumed.
      setAutopilotRunning(status.running || status.session?.status === 'running');
      if (status.portfolio) setAutopilotPortfolio(status.portfolio);
    } catch { /* non-fatal */ }
  }, []);

  const handleLaunchAutopilot = async (mode: AutopilotMode) => {
    setAutopilotLaunching(true);
    try {
      await tomiService.startAutopilot({ mode, capital: 100000, markets: ['equity', 'options'], use_options: true });
      await loadAutopilotStatus();
      setSuccessMsg(`Tomi Autopilot launched in ${mode} mode. First scan running now.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to launch autopilot');
    } finally {
      setAutopilotLaunching(false);
    }
  };

  const handleStopAutopilot = async () => {
    try {
      await tomiService.stopAutopilot();
      setAutopilotSession(null);
      setSuccessMsg('Tomi Autopilot stopped.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to stop autopilot');
    }
  };

  useEffect(() => {
    loadPortfolio();
    generateSignals();
    loadAutopilotStatus();
    // Poll autopilot status every 30 seconds
    const interval = setInterval(loadAutopilotStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Execute equity trade — notional dollar amount
  const handleExecuteTrade = async (signal: TomiSignal, amount: string) => {
    let order: unknown;
    if (signal.broker === 'alpaca') {
      order = await tomiService.placeEquityOrder({
        symbol: signal.symbol,
        side: 'buy',
        notional: amount,
        type: 'market',
        time_in_force: 'day',
      });
    } else {
      order = await tomiService.placeSpotOrder({
        product_id: signal.asset,
        side: 'BUY',
        quote_size: amount,
        order_type: 'market',
      });
    }

    const broker = signal.broker === 'alpaca' ? 'Alpaca' : 'Coinbase';
    setSuccessMsg(
      order
        ? `[${broker}] Order placed: ${signal.action} ${signal.symbol} — ${formatUsd(parseFloat(amount))}`
        : `[${broker}] Dry-run recorded: ${signal.action} ${signal.symbol} — ${formatUsd(parseFloat(amount))}`
    );

    setTimeout(() => loadPortfolio(), 2000);
    setTimeout(() => setSuccessMsg(null), 6000);
  };

  // Execute options order
  const handleExecuteOptionsOrder = async (signal: OptionsSignal) => {
    if (!signal.contract_symbol || signal.contracts === 0) {
      throw new Error('No valid options contract to execute');
    }
    const order = await tomiService.placeOptionsOrder({
      contract_symbol: signal.contract_symbol,
      qty: signal.contracts,
      side: 'buy',
      order_type: 'limit',
      limit_price: signal.ask_price > 0 ? String(signal.ask_price) : undefined,
    });

    setSuccessMsg(
      order
        ? `[Alpaca Options] Order placed: BUY ${signal.contracts}× ${signal.contract_symbol} @ ${formatUsd(signal.ask_price)}`
        : `[Alpaca Options] Dry-run recorded: ${signal.contracts}× ${signal.contract_symbol}`
    );

    setTimeout(() => loadPortfolio(), 2000);
    setTimeout(() => setSuccessMsg(null), 6000);
  };

  const actionableSignals = signalsData?.signals.filter(s => s.dollar_allocation > 0) || [];
  const allSignals = signalsData?.signals || [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.bg, color: COLORS.platinum, p: 3 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <AutoGraph sx={{ color: COLORS.gold, fontSize: 28 }} />
            <Typography variant="h4" sx={{ color: COLORS.platinum, fontWeight: 800, letterSpacing: -0.5 }}>
              Command Center
            </Typography>
          </Box>
          <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>
            富の神 × 風の洞察 —{' '}
            {market === 'crypto'  ? 'Fibonacci-Guided Crypto Trading' :
             market === 'equity'  ? 'Fibonacci-Guided Equity Trading · Alpaca Markets' :
             market === 'options' ? 'Options Trading · OTM Calls · $100k Paper Account · Alpaca' :
             'All Markets · Crypto + Equities · Cross-Asset Fibonacci'}
          </Typography>
          {lastGenerated && (
            <Typography sx={{ color: COLORS.muted, fontSize: 11, mt: 0.5 }}>
              Signals generated at {lastGenerated} ·{' '}
              <Typography component="span" sx={{ color: signalsData?.kaze_connected ? COLORS.green : COLORS.red, fontSize: 11 }}>
                Kaze {signalsData?.kaze_connected ? 'online' : 'offline'}
              </Typography>
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>

          {/* Market toggle */}
          <Box sx={{ display: 'flex', p: '3px', borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, gap: '2px' }}>
            {([
              { key: 'crypto',  label: 'Crypto',      icon: <CurrencyBitcoin sx={{ fontSize: 13 }} /> },
              { key: 'equity',  label: 'Equities',     icon: <ShowChart sx={{ fontSize: 13 }} /> },
              { key: 'all',     label: 'All Markets',  icon: <Business sx={{ fontSize: 13 }} /> },
              { key: 'options', label: 'Options',      icon: <Analytics sx={{ fontSize: 13 }} /> },
            ] as const).map(({ key, label, icon }) => (
              <Button
                key={key}
                size="small"
                startIcon={icon}
                onClick={() => {
                  setMarket(key);
                  setSignalsData(null);
                  // Default $100k capital in options mode
                  if (key === 'options' && capital < 10000) setCapital(100000);
                }}
                sx={{
                  px: 1.5, py: 0.5,
                  borderRadius: 1.5,
                  fontSize: 11,
                  fontWeight: market === key ? 700 : 400,
                  color: market === key ? '#000' : COLORS.muted,
                  bgcolor: market === key
                    ? key === 'options' ? '#9b59b6' : COLORS.gold
                    : 'transparent',
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  minWidth: 0,
                  '&:hover': {
                    bgcolor: market === key
                      ? key === 'options' ? '#8e44ad' : COLORS.goldLight
                      : `${COLORS.gold}18`,
                    color: market === key ? '#000' : COLORS.platinum,
                  },
                  '& .MuiButton-startIcon': { mr: 0.5 },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          {/* Capital input */}
          <TextField
            label="Capital ($)"
            value={capital}
            onChange={e => setCapital(Math.max(1, parseInt(e.target.value) || 200))}
            type="number"
            size="small"
            sx={{
              width: 120,
              '& .MuiOutlinedInput-root': {
                color: COLORS.platinum,
                '& fieldset': { borderColor: COLORS.border },
                '&:hover fieldset': { borderColor: COLORS.gold },
                '&.Mui-focused fieldset': { borderColor: COLORS.gold },
              },
              '& .MuiInputLabel-root': { color: COLORS.muted },
            }}
          />
          <Button
            onClick={generateSignals}
            disabled={loading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} /> : <Bolt />}
            sx={{
              bgcolor: COLORS.gold,
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: COLORS.goldLight },
              '&.Mui-disabled': { bgcolor: COLORS.goldDim, color: '#000' },
            }}
          >
            {loading ? 'Generating...' : 'Generate Signals'}
          </Button>
          <Tooltip title="Refresh portfolio">
            <IconButton
              onClick={loadPortfolio}
              disabled={portfolioLoading}
              sx={{ color: COLORS.muted, border: `1px solid ${COLORS.border}`, '&:hover': { color: COLORS.platinum } }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Tomi v2 Shadow Mode Panel ── */}
      <Box sx={{ mb: 3 }}>
        <TomiV2ShadowPanel />
      </Box>

      {/* ── Alerts ── */}
      {error && (
        <Alert severity="warning" onClose={() => setError(null)} sx={{ mb: 2, bgcolor: `${COLORS.amber}12`, color: COLORS.amber, border: `1px solid ${COLORS.amber}30` }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2, bgcolor: `${COLORS.green}12`, color: COLORS.green, border: `1px solid ${COLORS.green}30` }}>
          {successMsg}
        </Alert>
      )}

      <Grid container spacing={3}>

        {/* ── LEFT: Signal Feed ── */}
        <Grid item xs={12} lg={7}>

          {/* KPIs */}
          {signalsData && (
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              {[
                {
                  label: 'Capital',
                  value: formatUsd(signalsData.capital),
                  color: COLORS.platinum,
                  icon: <AccountBalance sx={{ fontSize: 18 }} />,
                },
                {
                  label: 'Allocated',
                  value: formatUsd(signalsData.total_allocated),
                  color: COLORS.gold,
                  icon: <TrendingUp sx={{ fontSize: 18 }} />,
                },
                {
                  label: 'Cash Reserve',
                  value: formatUsd(signalsData.cash_reserve),
                  color: COLORS.blue,
                  icon: <AccountBalance sx={{ fontSize: 18 }} />,
                },
                {
                  label: 'Signals Graded',
                  value: `${signalsData.tomi_evaluated} / ${allSignals.length}`,
                  color: COLORS.green,
                  icon: <Psychology sx={{ fontSize: 18 }} />,
                },
              ].map(item => (
                <Grid item xs={6} md={3} key={item.label}>
                  <Box sx={{
                    p: 1.5, borderRadius: 2,
                    bgcolor: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, color: COLORS.muted }}>
                      {item.icon}
                      <Typography sx={{ fontSize: 9, letterSpacing: 1.5 }}>{item.label.toUpperCase()}</Typography>
                    </Box>
                    <Typography sx={{ color: item.color, fontWeight: 700, fontSize: 17 }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Fibonacci allocation visualizer */}
          {signalsData && actionableSignals.length > 0 && (
            <Box sx={{ p: 2.5, mb: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <FibonacciAllocation
                signals={allSignals}
                capital={signalsData.capital}
                cashReserve={signalsData.cash_reserve}
              />
            </Box>
          )}

          {/* Signal list */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: COLORS.muted, letterSpacing: 2, textTransform: 'uppercase' }}>
                Tomi Graded Signals — {allSignals.length} assets evaluated
              </Typography>
              {market !== 'crypto' && (
                <Chip
                  icon={
                    market === 'equity'  ? <ShowChart sx={{ fontSize: 11 }} /> :
                    market === 'options' ? <Analytics sx={{ fontSize: 11 }} /> :
                    <Business sx={{ fontSize: 11 }} />
                  }
                  label={
                    market === 'equity'  ? 'ALPACA · US EQUITIES' :
                    market === 'options' ? 'ALPACA · OPTIONS · OTM CALLS' :
                    'CRYPTO + EQUITIES'
                  }
                  size="small"
                  sx={{
                    bgcolor: market === 'options' ? `#9b59b615` : `${COLORS.blue}15`,
                    color: market === 'options' ? '#9b59b6' : COLORS.blue,
                    border: `1px solid ${market === 'options' ? '#9b59b630' : `${COLORS.blue}30`}`,
                    fontSize: 9,
                    height: 20,
                    '& .MuiChip-icon': { color: market === 'options' ? '#9b59b6' : COLORS.blue },
                  }}
                />
              )}
            </Box>

            {loading && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress sx={{ color: COLORS.gold, mb: 2 }} size={40} />
                <Typography sx={{ color: COLORS.muted }}>
                  Kaze is reading the market · Tomi is pricing the edge...
                </Typography>
              </Box>
            )}

            {!loading && allSignals.length === 0 && (
              <Box sx={{ py: 6, textAlign: 'center', border: `1px dashed ${COLORS.border}`, borderRadius: 2 }}>
                <Psychology sx={{ color: COLORS.muted, fontSize: 40, mb: 1, display: 'block', mx: 'auto' }} />
                <Typography sx={{ color: COLORS.muted }}>
                  Press "Generate Signals" to activate Tomi's evaluation engine
                </Typography>
              </Box>
            )}

            {!loading && allSignals.map((signal, i) => {
              const rank = actionableSignals.findIndex(s => s.asset === signal.asset);
              if (market === 'options' && (signal as OptionsSignal).has_options !== undefined) {
                return (
                  <OptionsSignalCard
                    key={signal.asset}
                    signal={signal as OptionsSignal}
                    rank={rank >= 0 ? rank : i}
                    onSelect={setSelectedOptionsSignal}
                  />
                );
              }
              return (
                <SignalCard
                  key={signal.asset}
                  signal={signal}
                  rank={rank >= 0 ? rank : i}
                  onSelect={setSelectedSignal}
                />
              );
            })}
          </Box>
        </Grid>

        {/* ── RIGHT: Portfolio + Learning ── */}
        <Grid item xs={12} lg={5}>

          {/* Options positions — shown when market is options */}
          {market === 'options' && (
            <OptionsPositionsPanel positions={optionsPositions} loading={portfolioLoading} />
          )}

          {/* Alpaca equity portfolio — shown when market is equity or all */}
          {(market === 'equity' || market === 'all') && (
            <AlpacaPortfolioPanel positions={equityPortfolio} loading={portfolioLoading} />
          )}

          {/* Portfolio holdings — Coinbase crypto */}
          {(market === 'crypto' || market === 'all') && (
          <Box sx={{ p: 2.5, mb: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CurrencyBitcoin sx={{ color: COLORS.gold, fontSize: 18 }} />
              <Typography sx={{ color: COLORS.muted, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>
                Coinbase Portfolio
              </Typography>
            </Box>

            {portfolioLoading && <CircularProgress size={24} sx={{ color: COLORS.gold, display: 'block', mx: 'auto', my: 2 }} />}

            {!portfolioLoading && portfolio && (
              <>
                {portfolio.spot.length === 0 ? (
                  <Typography sx={{ color: COLORS.muted, fontSize: 12, textAlign: 'center', py: 2 }}>
                    No holdings found · Load capital to begin
                  </Typography>
                ) : (
                  <Box>
                    {portfolio.spot.map(acc => (
                      <Box
                        key={acc.uuid}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1.25,
                          borderBottom: `1px solid ${COLORS.border}`,
                          '&:last-child': { borderBottom: 0 },
                        }}
                      >
                        <Box>
                          <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 13 }}>
                            {acc.currency}
                          </Typography>
                          <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
                            {acc.name}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ color: COLORS.platinum, fontWeight: 600, fontSize: 13 }}>
                            {parseFloat(acc.available_balance?.value || '0').toFixed(
                              acc.currency === 'USD' ? 2 : 6
                            )} {acc.currency}
                          </Typography>
                          {parseFloat(acc.hold?.value || '0') > 0 && (
                            <Typography sx={{ color: COLORS.amber, fontSize: 10 }}>
                              Hold: {parseFloat(acc.hold.value).toFixed(4)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Trade cap */}
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${COLORS.border}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>Trade Capacity</Typography>
                    <Typography sx={{ color: COLORS.muted, fontSize: 10 }}>
                      {portfolio.tradeCount} / {portfolio.maxTrades} used
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(portfolio.tradeCount / portfolio.maxTrades) * 100}
                    sx={{
                      height: 5,
                      borderRadius: 3,
                      bgcolor: COLORS.border,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: portfolio.remaining < 20 ? COLORS.red : COLORS.green,
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
          )}

          {/* Fibonacci legend */}
          <Box sx={{ p: 2.5, mb: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', mb: 2, display: 'block' }}>
              φ Fibonacci Allocation Framework
            </Typography>
            {FIB_LEVELS.map((level) => (
              <Box key={level.ratio} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: level.color, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: level.color, fontSize: 11, fontWeight: 600 }}>
                      {level.desc} · {level.label}
                    </Typography>
                    <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                      ${(capital * level.ratio).toFixed(0)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={level.ratio * 100}
                    sx={{
                      height: 3,
                      borderRadius: 2,
                      bgcolor: COLORS.border,
                      '& .MuiLinearProgress-bar': { bgcolor: level.color },
                    }}
                  />
                </Box>
              </Box>
            ))}
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>Cash Reserve (~9%)</Typography>
              <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>${(capital * 0.09).toFixed(0)}</Typography>
            </Box>
            <Typography sx={{ color: COLORS.muted, fontSize: 10, mt: 1.5, fontStyle: 'italic' }}>
              Highest Tomi conviction receives Primary allocation. Each subsequent position follows the golden ratio sequence.
            </Typography>
          </Box>

          {/* Tomi Autopilot */}
          <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${autopilotSession?.status === 'running' ? '#4caf5050' : COLORS.border}` }}>
            <AutopilotPanel
              session={autopilotSession}
              running={autopilotRunning}
              portfolio={autopilotPortfolio}
              onLaunch={handleLaunchAutopilot}
              onStop={handleStopAutopilot}
              launching={autopilotLaunching}
            />
          </Box>

          {/* Learning stats */}
          <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <LearningPanel stats={learningStats} />
          </Box>

        </Grid>
      </Grid>

      {/* ── Guided Trade Dialog (equity / crypto) ── */}
      <GuidedTradeDialog
        signal={selectedSignal}
        onClose={() => setSelectedSignal(null)}
        onConfirm={handleExecuteTrade}
        capital={capital}
      />

      {/* ── Options Trade Dialog ── */}
      <OptionsTradeDialog
        signal={selectedOptionsSignal}
        onClose={() => setSelectedOptionsSignal(null)}
        onConfirm={handleExecuteOptionsOrder}
      />
    </Box>
  );
}
